import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { fmtDate, fmtRelative } from '@/features/dossier/components/primitives'
import { listCampaigns, type Campaign } from './api'
import { Cost, Reach, StatusChip } from './components'

/**
 * Every campaign, with its approver on the row.
 *
 * That column is not decoration. This module's central control is that a send has a named human
 * behind it, and a list that showed status without ownership would make the control invisible
 * exactly where somebody scans for it.
 */
export function CampaignsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Campaign[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listCampaigns())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading campaigns" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Campaigns"
        description="Every send, who approved it, how far it reached and what it cost."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <ErpTable head={<>
          <Th>Campaign</Th>
          <Th>Channel</Th>
          <Th>Reach</Th>
          <Th className="axis-hide-sm">Cost</Th>
          <Th className="axis-hide-sm">Approved by</Th>
          <Th>Status</Th>
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={6}
                      message="No campaigns yet. Compose one — it stays a draft until somebody
                               approves it." />
          )}
          {rows.map((c) => (
            <tr key={c.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/comms/campaigns/${c.id}`)}>
              <Td>
                <span className="font-medium">{c.displayName}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {c.ref} · {fmtRelative(c.createdAt)}
                </span>
              </Td>
              <Td>
                <span className="text-[11px]">{c.channelName ?? '—'}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {c.templateName ?? 'no template'}
                </span>
              </Td>
              <Td>
                <Reach recipients={c.recipientCount} suppressed={c.suppressedCount} />
              </Td>
              <Td className="axis-hide-sm">
                <Cost value={c.actualCostKes ?? c.estimatedCostKes}
                      prefix={c.actualCostKes ? '' : 'est. '} />
              </Td>
              <Td className="axis-hide-sm">
                {c.approvedBy
                  ? (
                    <>
                      <span className="text-[11px]">{c.approvedBy.slice(0, 8)}</span>
                      <span className="block text-[10px] text-muted-foreground">
                        {fmtDate(c.approvedAt)}
                      </span>
                    </>
                  )
                  : <span className="text-[10px] text-muted-foreground">not approved</span>}
              </Td>
              <Td>
                <StatusChip code={c.statusCode} />
                {c.stoppedReason && (
                  <span className="block text-[10px] text-muted-foreground">
                    {c.stoppedReason}
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
